import { useEffect, useState } from "react";
import { Trophy } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { api } from "../lib/api";
import { money } from "../lib/format";
import { Panel, Empty } from "./Widgets";

const short = (s) => (s.length > 22 ? `${s.slice(0, 21)}…` : s);

export function TopMerchants() {
  const [year, setYear] = useState("latest");
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/top-merchants", { params: { limit: 20, year: year || undefined } }).then((r) => setD(r.data)); }, [year]);
  const rows = d?.items;
  const data = (rows || []).map((r) => ({ ...r, label: short(r.merchant_name || r.merchant_id) }));
  const label = d?.year ? `${d.year} residual` : "Lifetime residual";
  const picker = (
    <div className="flex items-center gap-2">
      <select data-testid="top-merchants-year" value={year === "latest" ? d?.year || "" : year} onChange={(e) => setYear(e.target.value)}
        className="h-8 rounded-md bg-[#0B0F17] border border-slate-700 text-xs px-2">
        <option value="">Lifetime</option>
        {(d?.years || []).map((y) => <option key={y} value={y}>{y}</option>)}
      </select>
      <Trophy size={16} className="text-amber-400" />
    </div>
  );
  return (
    <Panel title={`Top 20 merchants · ${d?.year || "lifetime"}`} className="mb-6" action={picker}>
      {!rows ? <Empty>Loading…</Empty> : rows.length === 0 ? <Empty>No merchants yet.</Empty> : (
        <div className="grid lg:grid-cols-5 gap-6 p-5" data-testid="top-merchants-panel">
          <div className="lg:col-span-3 h-[520px]" data-testid="top-merchants-chart">
            <ResponsiveContainer>
              <BarChart data={data} layout="vertical" margin={{ left: 10, right: 20 }}>
                <CartesianGrid stroke="#1E293B" horizontal={false} />
                <XAxis type="number" tickFormatter={(v) => `$${Math.round(v).toLocaleString()}`} stroke="#64748B" fontSize={11} />
                <YAxis type="category" dataKey="label" width={150} stroke="#94A3B8" fontSize={11} interval={0} />
                <Tooltip cursor={{ fill: "#1E293B" }} labelStyle={{ color: "#F1F5F9" }} itemStyle={{ color: "#E2E8F0" }}
                  contentStyle={{ background: "#0B0F17", border: "1px solid #334155", borderRadius: 6 }} formatter={(v) => money(v)} />
                <Bar dataKey="lifetime_residual" name={label} fill="#10B981" radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <ol className="lg:col-span-2 space-y-1 text-sm max-h-[520px] overflow-y-auto pr-2" data-testid="top-merchants-list">
            {rows.map((r, i) => (
              <li key={r.merchant_id} data-testid={`top-merchant-${r.merchant_id}`} className="flex items-baseline gap-3 border-b border-slate-800/70 py-1.5">
                <span className="font-mono text-xs text-slate-500 w-5">{i + 1}</span>
                <span className="flex-1 min-w-0">
                  <span className="block truncate text-slate-100">{r.merchant_name || "—"}</span>
                  <span className="block text-[11px] text-slate-500 truncate">{r.owner} · {r.months} mo</span>
                </span>
                <span className="font-mono text-emerald-400">{money(r.lifetime_residual)}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </Panel>
  );
}
